import type { Team, TeamAssignmentStrategy } from "@/types/team";

import { createId } from "../generators/base";
import { toTeamMember } from "../generators/team";
import { useMockStore } from "../store";

/**
 * Get all teams for the active workspace.
 */
export function getTeamsMock(): Team[] {
  return useMockStore.getState().teams;
}

interface CreateTeamPayload {
  name: string;
  description?: string;
  color?: string;
  assignmentStrategy?: TeamAssignmentStrategy;
  userIds?: string[];
}

/**
 * Create a team, optionally seeding it with members from the given user ids.
 */
export function createTeamMock(payload: CreateTeamPayload): Team {
  const store = useMockStore.getState();

  const members = (payload.userIds ?? [])
    .map((userId) => store.users.find((user) => user.id === userId))
    .filter((user): user is NonNullable<typeof user> => Boolean(user))
    .map(toTeamMember);

  const team: Team = {
    id: createId(),
    name: payload.name,
    description: payload.description ?? null,
    color: payload.color ?? null,
    assignmentStrategy: payload.assignmentStrategy ?? "round_robin",
    members,
    memberCount: members.length,
  };

  store.addTeam(team);

  return team;
}

interface UpdateTeamPayload {
  name?: string;
  description?: string;
  color?: string;
  assignmentStrategy?: TeamAssignmentStrategy;
  userIds?: string[];
}

/**
 * Update a team. When `userIds` is supplied the membership is replaced.
 */
export function updateTeamMock(
  teamId: string,
  payload: UpdateTeamPayload,
): Team | undefined {
  const store = useMockStore.getState();
  const team = store.teams.find((t) => t.id === teamId);

  if (!team) return undefined;

  const members =
    payload.userIds !== undefined
      ? payload.userIds
          .map((userId) => store.users.find((user) => user.id === userId))
          .filter((user): user is NonNullable<typeof user> => Boolean(user))
          .map(toTeamMember)
      : team.members;

  store.updateTeam(teamId, {
    ...(payload.name !== undefined ? { name: payload.name } : {}),
    ...(payload.description !== undefined
      ? { description: payload.description }
      : {}),
    ...(payload.color !== undefined ? { color: payload.color } : {}),
    ...(payload.assignmentStrategy !== undefined
      ? { assignmentStrategy: payload.assignmentStrategy }
      : {}),
    members,
    memberCount: members.length,
  });

  return store.teams.find((t) => t.id === teamId);
}

export function deleteTeamMock(teamId: string): void {
  useMockStore.getState().deleteTeam(teamId);
}

export function addTeamMemberMock(
  teamId: string,
  userId: string,
): Team | undefined {
  const store = useMockStore.getState();
  const user = store.users.find((u) => u.id === userId);

  if (user) {
    store.addTeamMember(teamId, toTeamMember(user));
  }

  return store.teams.find((t) => t.id === teamId);
}

export function removeTeamMemberMock(teamId: string, userId: string): void {
  useMockStore.getState().removeTeamMember(teamId, userId);
}
